import React, { useEffect, useMemo, useState } from 'react';
import { Download, Loader2, ShieldAlert } from 'lucide-react';
import { Modal } from './Modal';
import { useToast } from './ToastContext';
import { AmministrazioneService } from '../services/apiService';
import { localIsoDate } from '../utils/utils';

interface Props {
    onClose: () => void;
}

// Etichette leggibili per le categorie definite dal BE; quelle sconosciute
// vengono mostrate con la chiave grezza.
const CATEGORY_LABELS: Record<string, { label: string; hint: string }> = {
    audit_logs: {
        label: 'Audit log applicativo',
        hint: 'Azioni utente registrate (login, azioni, admin). Dettagli JSON redatti.',
    },
    client_errors: {
        label: 'Errori client',
        hint: 'Errori riportati dal web e dall\'app.',
    },
    escalations: {
        label: 'Notifiche di escalation',
        hint: 'Reminder e MISS_START/STOP inviati su incarichi e turni + eventuale ACK.',
    },
    orari_difformi: {
        label: 'Storico difformità orarie',
        hint: 'Scostamenti tra orario pianificato e reale. Un record per giorno pianificato.',
    },
    users: {
        label: 'Anagrafica utenti (redatta)',
        hint: 'Ruoli, sedi, tariffe. Email/nome/cognome/telefono mascherati.',
    },
    sedi: {
        label: 'Elenco sedi',
        hint: 'Solo id + nome. Recapiti e token operativi esclusi.',
    },
    settings: {
        label: 'Snapshot impostazioni tecniche',
        hint: 'Solo tariffe/soglie/flag. Credenziali, dati agenzia e coordinate bancarie esclusi.',
    },
    app_log_file: {
        label: 'File di log applicativo',
        hint: 'Ultimi MB di Backend.log così come sono scritti sul server (raw).',
    },
    audit_log_file: {
        label: 'File di log audit',
        hint: 'Ultimi MB dell\'audit hot-file (raw).',
    },
    snapshot: {
        label: 'Snapshot completo DB',
        hint: 'Un file Avro per tabella (nomi anonimizzati NBxxxx). Id/timestamp/importi/enum grezzi; le colonne testuali con contenuto libero appaiono col suffisso __redacted nello schema. I movimenti contabili (dati_aggiuntivi_json di pratiche e servizi) sono conservati con struttura e flag ma importi scramblati e testi/PII rimossi, così la Contabilità è riproducibile. Tutte le righe (nessun cap).',
    },
};

export const LogExportModal: React.FC<Props> = ({ onClose }) => {
    const { addToast } = useToast();

    const today = localIsoDate();
    const sevenDaysAgo = localIsoDate(new Date(Date.now() - 6 * 24 * 3600 * 1000));

    const [from, setFrom] = useState<string>(sevenDaysAgo);
    const [to, setTo] = useState<string>(today);
    const [categories, setCategories] = useState<string[]>([]);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [loadingCategories, setLoadingCategories] = useState(true);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const cats = await AmministrazioneService.listLogExportCategories();
                if (cancelled) return;
                setCategories(cats);
                setSelected(new Set(cats));
            } catch (e: any) {
                addToast('Impossibile caricare le categorie di export', 'error');
            } finally {
                if (!cancelled) setLoadingCategories(false);
            }
        })();
        return () => { cancelled = true; };
    }, [addToast]);

    const allSelected = useMemo(() =>
        categories.length > 0 && selected.size === categories.length,
        [categories, selected]);

    const toggle = (key: string) => {
        const next = new Set(selected);
        if (next.has(key)) next.delete(key); else next.add(key);
        setSelected(next);
    };

    const selectAll = () => setSelected(new Set(categories));
    const selectNone = () => setSelected(new Set());

    const handleExport = async () => {
        if (selected.size === 0) {
            addToast('Seleziona almeno una categoria', 'error');
            return;
        }
        if (!from || !to) {
            addToast('Imposta un range di date valido', 'error');
            return;
        }
        if (from > to) {
            addToast("'Da' deve essere <= 'A'", 'error');
            return;
        }
        setExporting(true);
        try {
            await AmministrazioneService.exportLogsZip({
                from, to,
                categories: Array.from(selected),
            });
            addToast('Download avviato', 'success');
            onClose();
        } catch (e: any) {
            addToast('Errore durante l\'export: ' + (e?.message ?? 'sconosciuto'), 'error');
        } finally {
            setExporting(false);
        }
    };

    return (
        <Modal title="Esporta log di sistema" onClose={exporting ? undefined : onClose}>
            <div className="space-y-5">
                {/* Range date */}
                <div className="grid grid-cols-2 gap-3">
                    <label className="flex flex-col gap-1 text-sm">
                        <span className="font-semibold text-slate-700">Da</span>
                        <input
                            type="date"
                            value={from}
                            max={to}
                            onChange={e => setFrom(e.target.value)}
                            disabled={exporting}
                            className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                        />
                    </label>
                    <label className="flex flex-col gap-1 text-sm">
                        <span className="font-semibold text-slate-700">A</span>
                        <input
                            type="date"
                            value={to}
                            min={from}
                            max={today}
                            onChange={e => setTo(e.target.value)}
                            disabled={exporting}
                            className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                        />
                    </label>
                </div>

                {/* Redazione + scrambler + cifratura */}
                <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-lg p-3 flex gap-2">
                    <ShieldAlert size={16} className="flex-shrink-0 mt-0.5"/>
                    <span>
                        Redazione stretta (PII oscurate; id/timestamp raw). Importi economici
                        scramblati con fattore casuale non serializzato — rapporti indagabili, valori
                        assoluti no. Payload Avro OCF cifrato AES-256-GCM per entry: senza chiave
                        nessun tool né AI può leggerli.
                    </span>
                </div>

                {/* Categorie */}
                <div>
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-slate-700">Categorie da includere</span>
                        <div className="flex gap-2 text-xs">
                            <button
                                type="button"
                                onClick={selectAll}
                                disabled={loadingCategories || exporting || allSelected}
                                className="text-indigo-600 font-semibold hover:underline disabled:text-slate-400 disabled:no-underline"
                            >
                                Tutte
                            </button>
                            <span className="text-slate-300">·</span>
                            <button
                                type="button"
                                onClick={selectNone}
                                disabled={loadingCategories || exporting || selected.size === 0}
                                className="text-indigo-600 font-semibold hover:underline disabled:text-slate-400 disabled:no-underline"
                            >
                                Nessuna
                            </button>
                        </div>
                    </div>
                    <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-72 overflow-y-auto">
                        {loadingCategories && (
                            <div className="p-3 text-sm text-slate-500 flex items-center gap-2">
                                <Loader2 size={14} className="animate-spin"/> Caricamento categorie…
                            </div>
                        )}
                        {!loadingCategories && categories.map(key => {
                            const meta = CATEGORY_LABELS[key] ?? { label: key, hint: '' };
                            const checked = selected.has(key);
                            return (
                                <label
                                    key={key}
                                    className={`flex items-start gap-3 p-3 cursor-pointer select-none hover:bg-slate-50 transition-colors ${exporting ? 'opacity-60 pointer-events-none' : ''}`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => toggle(key)}
                                        className="mt-0.5 w-5 h-5 rounded border-slate-300 text-indigo-600"
                                    />
                                    <span className="flex-1">
                                        <span className="block text-sm font-bold text-slate-800">{meta.label}</span>
                                        {meta.hint && (
                                            <span className="block text-[11px] text-slate-500 mt-0.5">{meta.hint}</span>
                                        )}
                                        <span className="block text-[10px] text-slate-400 mt-0.5 font-mono">{key}</span>
                                    </span>
                                </label>
                            );
                        })}
                        {!loadingCategories && categories.length === 0 && (
                            <div className="p-3 text-sm text-slate-500">
                                Nessuna categoria disponibile.
                            </div>
                        )}
                    </div>
                </div>

                {/* Azioni */}
                <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={exporting}
                        className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
                    >
                        Annulla
                    </button>
                    <button
                        type="button"
                        onClick={handleExport}
                        disabled={exporting || loadingCategories || selected.size === 0}
                        className="px-4 py-2 rounded-lg text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        {exporting ? <Loader2 size={16} className="animate-spin"/> : <Download size={16}/>}
                        {exporting ? 'Genero ZIP…' : 'Esporta ZIP'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};
